import { faCalendar, faSliders, faCopy, faPlay, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { ShippingRulePreset } from '../../models/ShippingRulePreset';

interface PresetCardProps {
  preset: ShippingRulePreset;
  onApply: (preset: ShippingRulePreset) => void;
  onEdit: (preset: ShippingRulePreset) => void;
  onDuplicate: (preset: ShippingRulePreset) => void;
  onDelete: (preset: ShippingRulePreset) => void;
}

// Formatea una fecha en el idioma local
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Tarjeta de un preset de reglas de envío con sus acciones principales
export const PresetCard = ({ preset, onApply, onEdit, onDuplicate, onDelete }: PresetCardProps) => {
  const { t } = useTranslation('shippingRulePresetsModule');

  return (
    <div className="flex flex-col rounded-xl border border-card-border bg-card-bg p-5 gap-4 hover:border-primary/40 transition-colors">
      {/* Nombre y descripción del preset */}
      <div className="flex flex-col gap-1">
        <h3 className="flex items-center gap-2 text-base font-semibold text-title leading-snug">
          <FontAwesomeIcon icon={faSliders} className="w-3.5 h-3.5 text-icons flex-shrink-0" />
          <span className="truncate">{preset.name}</span>
        </h3>
        {preset.description && <p className="text-sm text-subtext line-clamp-2">{preset.description}</p>}
      </div>

      {/* Badge de cantidad de reglas */}
      <div className="flex items-center">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-card-border text-subtext">
          {t('card.rules', { count: preset.rules_count })}
        </span>
      </div>

      {/* Fecha (solo una) con icono, sin labels */}
      <div className="flex items-center gap-2 text-xs text-subtext">
        <FontAwesomeIcon icon={faCalendar} className="w-3.5 h-3.5 text-icons" />
        <span>{formatDate(preset.updated_at)}</span>
      </div>

      {/* Acciones: botón principal Aplicar + acciones secundarias */}
      <div className="border-t border-card-border pt-3 flex items-center gap-2 flex-wrap mt-auto">
        {/* Acción principal: Aplicar */}
        <Button
          className="flex-1 min-w-[100px]"
          onClick={() => onApply(preset)}
          data-testid={`apply-preset-${preset.id}-button`}
        >
          <FontAwesomeIcon icon={faPlay} className="w-3.5 h-3.5 mr-2" />
          {t('buttons.apply')}
        </Button>

        {/* Acción: Editar */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onEdit(preset)}
          title={t('buttons.edit')}
          data-testid={`edit-preset-${preset.id}-button`}
        >
          <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5" />
        </Button>

        {/* Acción: Duplicar */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onDuplicate(preset)}
          title={t('buttons.duplicate')}
          data-testid={`duplicate-preset-${preset.id}-button`}
        >
          <FontAwesomeIcon icon={faCopy} className="w-3.5 h-3.5" />
        </Button>

        {/* Acción: Eliminar */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onDelete(preset)}
          title={t('buttons.delete')}
          data-testid={`delete-preset-${preset.id}-button`}
          className="text-error hover:text-error hover:border-error/50"
        >
          <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};
